import type { PostHogContextRef } from '../context'

/**
 * Which profile carousel emitted the error. Aligned 1:1 with the two
 * federated queries that feed CommunityProfilePage — GET_PROFILE_IN_PROGRESS_CAROUSEL
 * and GET_PROFILE_WATCH_HISTORY_CAROUSEL. Add a new id here when wiring a
 * new error-aware carousel on the profile surface.
 */
export type CommunityCarouselId = 'in_progress' | 'watch_history'

/**
 * Profile carousel data-load failures. Fires once per carousel per page
 * load when its GraphQL query returns an error. Distinct from page-level
 * errors — the carousel renders an inline message and the rest of the
 * profile loads normally, so the event is the only signal that the
 * federated resolver failed.
 *
 * Both target queries are configured with `errorPolicy: 'all'`, so partial
 * data may still arrive alongside the error.  When that partial data is
 * empty, the inline error renders; either way the event fires so the
 * silent-failure mode (errorPolicy:'all' + only-`data` destructuring)
 * cannot recur.
 */
export type CarouselErrorEvents = {
  community_carousel_error: (carousel: CommunityCarouselId, errorMessage: string) => void
}

export function createCarouselErrorEventCallbacks (
  contextRef: PostHogContextRef,
): CarouselErrorEvents {
  return {
    community_carousel_error: (carousel: CommunityCarouselId, errorMessage: string) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('community_carousel_error', {
        context: 'community',
        carousel,
        error_message: errorMessage,
      })
    },
  }
}
